import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2, Upload } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { setCfcData } from "../../../store/slices/cfcSlice";
import api from "@/utils/api";
import ApplicationPreview from "./ApplicationPreview";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import declarationDocument from "../../../assets/documents/spv-declaration.pdf";

const declarationSchema = z.object({
  terms_accepted: z.boolean().refine((val) => val === true, {
    message: "You must accept the certification",
  }),
  applicant_signature: z
    .any()
    .refine(
      (file) => file instanceof File || typeof file === "string",
      "Signature is required"
    ),
  declaration_document: z
    .any()
    .refine(
      (file) => file instanceof File || typeof file === "string",
      "Declaration Document is required"
    ),
});

const DeclarationDetails = ({ data }) => {
  const dispatch = useDispatch();
  const nav = useNavigate();

  const [filePreview, setFilePreview] = useState(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [thankyouModal, setThankyouModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(declarationSchema),
    defaultValues: {
      terms_accepted: data?.declaration?.terms_accepted || false,
      applicant_signature: data?.declaration?.applicant_signature || "",
      declaration_document: data?.declaration?.declaration_document || "",
    },
  });

  const formData = watch();

  useEffect(() => {
    if (data?.declaration) {
      setFilePreview({
        applicant_signature: data?.declaration?.applicant_signature || "",
        declaration_document: data?.declaration?.declaration_document || "",
      });
    }
  }, [data]);

  const handleFileUpload = async (name, file) => {
    if (file) {
      setValue(name, file, { shouldValidate: true });

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(file);
      }

      await trigger(name);
    }
  };

  const handleInputChange = async (name, value) => {
    setValue(name, value, { shouldValidate: true });
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const isValid = await trigger();
      if (!isValid) {
        toast({
          title: "Validation Error",
          description: "Please fix errors before saving",
          variant: "destructive",
        });
        return;
      }

      const submitData = new FormData();
      Object.keys(formData).forEach((key) => {
        let value = formData[key];

        if (typeof value === "boolean") {
          value = value ? 1 : 0;
        }

        if (value !== undefined && value !== null) {
          submitData.append(key, value);
        }
      });

      submitData.append("id", data?.id || "");

      const response = await api.post(
        "/cfc-subsidies/declaration_details",
        submitData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      await dispatch(setCfcData(response?.data?.data || {}));

      toast({
        title: "Saved",
        description:
          'Declaration saved successfully. Click "Save & Next" to persist to server.',
        variant: "success",
      });

      setThankyouModal(true);
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setConfirmationModalOpen(false);
      setIsSubmitting(false);
    }
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* <ApplicationPreview application={data} /> */}
      <Card>
        <CardHeader>
          <CardTitle>Terms and Conditions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4 border-t pt-4">
            <div className="prose prose-sm max-w-none p-4 bg-muted rounded-md overflow-y-auto">
              {/* <h4 className="font-semibold">The SPV certify that:</h4> */}
              <ol className="list-decimal pl-4 space-y-2 text-sm">
                {/* <li>The information furnished above is true and correct.</li> */}
                <li>
                  The subsidy provided will be used only for the proposed CFC.
                </li>
                <li>
                  The SPV should not avail/availed subsidy for the same purpose
                  from any other scheme of the Central / State Government. if it
                  so,the application of the SPV will be summarily rejected, if
                  found later, the entire amount of subsidy will be forfeited by
                  the Government.
                </li>
                <li>
                  Any variation found will result in forfeiting the benefits.
                </li>
                <li>
                  The SPV should not have been blacklisted by any Government
                  agency.
                </li>
                <li>
                  Submission of false information may lead to cancellation of
                  application and legal action.
                </li>
                <li>
                  The decision of the sanctioning authority will be final and
                  binding.
                </li>
                <li>
                  The scheme is subject to availability of budget allocation.
                </li>
                <li>
                  All statutory clearances and permissions are the
                  responsibility of the SPV.
                </li>
                <li>
                  The SPV should take the responsibility for any omissions and
                  misrepresentations in the application.
                </li>
                <li>
                  The SPV insisted that the components benefited under this
                  scheme should not be sold within 5 years of the date of final
                  sanction.
                </li>
              </ol>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms_accepted"
                checked={formData.terms_accepted}
                onCheckedChange={(checked) =>
                  handleInputChange("terms_accepted", checked)
                }
              />
              <Label htmlFor="terms_accepted" className="text-sm">
                I have read and understood the above terms and conditions.{" "}
                <span className="text-red-500">*</span>
              </Label>
            </div>

            {errors.terms_accepted && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {errors.terms_accepted.message}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="declaration_document">
              Declaration Document <span className="text-red-500">*</span>
              <Button
                type="button"
                variant="link"
                onClick={() =>
                  handleDownload(declarationDocument, `declaration.pdf`)
                }
              >
                (Download Declaration document)
              </Button>
            </Label>
            <div className="border-2 border-white border-dashed rounded-lg p-4">
              <div className="flex items-center gap-4">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) =>
                      handleFileUpload(
                        "declaration_document",
                        e.target.files[0]
                      )
                    }
                    className="hidden"
                    id="declaration_document"
                  />

                  <Label
                    htmlFor="declaration_document"
                    className="cursor-pointer text-sm"
                  >
                    Click to upload Declaration Document
                  </Label>

                  {formData.declaration_document && (
                    <p className="text-sm text-green-600 mt-2">
                      Uploaded: {formData.declaration_document.name}
                    </p>
                  )}

                  {filePreview?.declaration_document && (
                    <img
                      src={filePreview?.declaration_document}
                      alt="Preview"
                      className="mt-2 max-h-32"
                    />
                  )}

                  {errors.declaration_document && (
                    <p className="text-sm text-red-500">
                      {errors.declaration_document.message}
                    </p>
                  )}
                </div>
                <p className="ms-1 text-[12px] text-muted-foreground">
                  <b>
                    Download the declaration document, take a printout, and sign
                    it with the date and place and Upload.
                  </b>
                  <br />
                  Supported only PDF, JPG, JPEG, PNG Files max size 2MB
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="applicant_signature">
              Signature of the SPV Members{" "}
              <span className="text-red-500">*</span>
            </Label>
            <div className="border-2 border-white border-dashed rounded-lg p-4">
              <div className="flex items-center gap-4">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleFileUpload("applicant_signature", e.target.files[0])
                    }
                    className="hidden"
                    id="applicant_signature"
                  />

                  <Label
                    htmlFor="applicant_signature"
                    className="cursor-pointer text-sm"
                  >
                    Click to upload SPV members signature (only image)
                  </Label>

                  {formData.applicant_signature && (
                    <p className="text-sm text-green-600 mt-2">
                      Uploaded: {formData.applicant_signature.name}
                    </p>
                  )}

                  {filePreview?.applicant_signature && (
                    <img
                      src={filePreview?.applicant_signature}
                      alt="Preview"
                      className="mt-2 max-h-32"
                    />
                  )}

                  {errors.applicant_signature && (
                    <p className="text-sm text-red-500">
                      {errors.applicant_signature.message}
                    </p>
                  )}
                </div>
                <span className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF Files max size 2MB
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6 gap-3">
            {/* <Button
              type="button"
              onClick={() =>
                handleDownload(
                  data?.preview_application_url,
                  `${data?.application_no}.pdf`
                )
              }
              disabled={!data?.preview_application_url}
              className="min-w-[150px] bg-blue-600 hover:bg-blue-700"
            >
              Preview Application
            </Button> */}
            <Button
              type="button"
              onClick={async () => {
                const isValid = await trigger();
                if (isValid) {
                  setConfirmationModalOpen(true);
                } else {
                  setConfirmationModalOpen(false);
                  toast({
                    title: "Validation Error",
                    description:
                      "Please fix the highlighted errors before submitting.",
                    variant: "destructive",
                  });
                }
              }}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting Application...
                </>
              ) : (
                "Submit Application"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      <Dialog
        open={confirmationModalOpen}
        onOpenChange={setConfirmationModalOpen}
      >
        <DialogContent
          disableCloseButton
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Confirm Final Submission
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-3">
                <p className="font-medium text-foreground">
                  Are you sure you want to submit your application?
                </p>
                <ul className="list-disc pl-4 space-y-1 text-sm text-muted-foreground">
                  <li>This action cannot be undone</li>
                  <li>
                    You will not be able to edit your application after
                    submission
                  </li>
                  <li>Your application will be sent for review and approval</li>
                  <li>
                    Please ensure all information is correct before proceeding
                  </li>
                  <li>
                    If Application is found incorrect my application will be
                    rejected at any point of time
                  </li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setConfirmationModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Submit Application"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={thankyouModal} onOpenChange={setThankyouModal}>
        <DialogContent
          disableCloseButton
          className="sm:max-w-md"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Your Application Submitted Successfully
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-3">
                <p className="font-medium text-foreground">
                  Your Application Number is {data?.application_no}
                </p>
                <p className="text-foreground">
                  Your application will be sent to your concern district
                  officer. You receive a provisional sanction order once your
                  application is verified and approved by the district office.
                  You will also get a SMS and Email update for further status of
                  the application.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              type="button"
              onClick={() =>
                handleDownload(
                  data?.application_url,
                  `${data?.application_no}.pdf`
                )
              }
            >
              Download Application
            </Button>
            <Button
              type="button"
              onClick={() => {
                setThankyouModal(false);
                nav("/");
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              Go To DashBoard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default DeclarationDetails;
